import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import ClothedEyeLoader from './ClothedEyeLoader';
import { trackStickerEngagement } from '../services/analyticsService';
import './SuperSimpleStickerCard.css';

const SuperSimpleStickerCard = React.memo(({
  listingId,
  title,
  category,
  svgPath,
  firstImageUrl,
  largestSizeImageUrl,
  etsyUrl,
  onCardClick,
  onAddToScene,
  // Debug props for sales/views overlay
  totalSalesVolume,
  views,
  showDebugOverlay = false,
  debugType = 'sales' // 'sales' or 'views'
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle image loading
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    // Track successful image load
          // Removed image_load_success tracking as requested
  }, [listingId, category]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    // Track image load failure
          // Removed image_load_error tracking as requested
  }, [listingId, category]);

  // Get category collection fallback image
  const getCategoryFallbackImage = useCallback(() => {
    if (category) {
      // Strip " Stickers" suffix from category name first
      const cleanCategory = category.replace(/\s+stickers?$/i, '').trim();

      // Map category names to collection file names
      const categoryMappings = {
        'cryptids': 'cryptid',
        'cryptid': 'cryptid',
        'dinosaurs': 'dino',
        'dinosaur': 'dino',
        'dino': 'dino',
        'animals': 'animals',
        'animal': 'animals',
        'cartoons': 'cartoon',
        'cartoon': 'cartoon',
        'vehicles': 'vehicles',
        'vehicle': 'vehicles',
        'space': 'space',
        'military': 'military',
        'police/military/fire': 'military',
        'horror': 'horror',
        'horror classic': 'horror-classic',
        'holiday': 'holiday',
        'pop culture': 'popculture',
        'popculture': 'popculture',
        'vehicles pop culture': 'vehicles',
        'outdoor': 'outdoor',
        'offroad': 'offroad',
        'offroad slogans': 'offroad-slogans'
      };

      const categoryLower = cleanCategory.toLowerCase();
      const collectionName = categoryMappings[categoryLower] || categoryLower;
      return `/assets/images/collection-${collectionName}.png`;
    }
    return '/assets/svgs/clothedEye-watermark.svg';
  }, [category]);

  // Track card hover for analytics
  const handleCardHover = useCallback(() => {
    trackStickerEngagement(listingId, 'card_hover', category, 'N/A', title);
  }, [listingId, category, title]);

  // Track card click for analytics
  const handleCardClick = useCallback(() => {
    trackStickerEngagement(listingId, 'card_click', category, null, title);
    
    if (onCardClick) {
      onCardClick();
    }
  }, [listingId, category, onCardClick, title]);

  // Track add to scene for analytics
  const handleAddToScene = useCallback(() => {
    trackStickerEngagement(listingId, 'add_to_scene_from_simple_card', category, null, title);
    
    if (onAddToScene) {
      onAddToScene();
    }
  }, [listingId, category, onAddToScene, title]);

  return (
    <div className="super-simple-sticker-card">
      {/* Image Container */}
      <div className="super-simple-image-container">
        {(svgPath || firstImageUrl) && !imageError ? (
          <>
            <img
              src={svgPath || firstImageUrl}
              alt={title || 'Sticker'}
              className={`super-simple-image ${imageLoaded ? 'loaded' : ''}`}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {!imageLoaded && (
              <div className="super-simple-loading-overlay">
                <ClothedEyeLoader 
                  size="small" 
                  showTagline={false}
                  showLogo={false}
                  className="card-loader"
                />
              </div>
            )}
          </>
        ) : (
          <div className="super-simple-placeholder">
            <img
              src={getCategoryFallbackImage()}
              alt={category ? `${category} Collection` : 'ClothedEye'}
              className="placeholder-logo"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="super-simple-content">
        <h3 className="super-simple-title">
          {(title || 'Sticker').substring(0, 20)}
          {(title || 'Sticker').length > 20 && '...'}
        </h3>
        {category && (
          <p className="super-simple-category">{category}</p>
        )}
      </div>

      {/* Quick Add Button - no click handler needed, wrapper handles all clicks */}
      <button 
        className="super-simple-add-btn"
        title="Add to Scene"
      >
        <MaterialIcon icon="add" size={16} />
      </button>

      {/* Debug Overlay - Show sales/views numbers */}
      {showDebugOverlay && (
        <div className="debug-overlay">
          <div className="debug-number">
            {debugType === 'sales' ? (
              <>
                <span className="debug-label">SALES</span>
                <span className="debug-value">{totalSalesVolume || 0}</span>
              </>
            ) : (
              <>
                <span className="debug-label">VIEWS</span>
                <span className="debug-value">{views || 0}</span>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
});

SuperSimpleStickerCard.propTypes = {
  listingId: PropTypes.string.isRequired,
  title: PropTypes.string,
  category: PropTypes.string,
  svgPath: PropTypes.string,
  firstImageUrl: PropTypes.string,
  largestSizeImageUrl: PropTypes.string,
  etsyUrl: PropTypes.string,
  onCardClick: PropTypes.func
};

export default SuperSimpleStickerCard; 